/**
 * Denominational Documents API Client
 *
 * Provides access to official denominational documents like:
 * - Statement of Faith
 * - Official Manuals
 * - Articles of Religion
 * - Denominational policies and procedures
 */

import apiClient from './client';

export interface DenominationalDocument {
  id: string;
  user_id?: string;  // NULL = official document, UUID = personal document
  denomination_id: string;
  document_type: string;
  title: string;
  section_title?: string;
  section_number?: number;
  content: string;
  keywords?: string[];
  citation_format?: string;
  is_official: boolean;  // True if user_id is NULL (shared with all)
  created_at: string;
  updated_at: string;
}

export interface DocumentListResponse {
  documents: DenominationalDocument[];
  total: number;
}

export interface SearchRequest {
  query: string;
  denomination_id?: string;
  limit?: number;
  offset?: number;
}

export interface BulkUploadRequest {
  denomination_id: string;
  document_type: string;
  title: string;
  content: string;
  chunking_strategy?: 'articles' | 'paragraphs';
  max_words_per_chunk?: number;
}

/**
 * List denominational documents with optional filtering
 */
export const listDocuments = async (
  denominationId?: string,
  documentType?: string,
  limit: number = 100,
  offset: number = 0
): Promise<DocumentListResponse> => {
  const params = new URLSearchParams();

  if (denominationId) params.append('denomination_id', denominationId);
  if (documentType) params.append('document_type', documentType);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  const response = await apiClient.get<DocumentListResponse>(
    `/api/denominational-documents?${params.toString()}`
  );

  return response.data;
};

/**
 * Get a specific document by ID
 */
export const getDocument = async (documentId: string): Promise<DenominationalDocument> => {
  const response = await apiClient.get<DenominationalDocument>(
    `/api/denominational-documents/${documentId}`
  );

  return response.data;
};

/**
 * Search documents using full-text search
 */
export const searchDocuments = async (
  request: SearchRequest
): Promise<DocumentListResponse> => {
  const response = await apiClient.post<DocumentListResponse>(
    '/api/denominational-documents/search',
    request
  );

  return response.data;
};

/**
 * Create a new document section (admin only)
 */
export const createDocument = async (
  document: Omit<DenominationalDocument, 'id' | 'created_at' | 'updated_at'>
): Promise<DenominationalDocument> => {
  const response = await apiClient.post<DenominationalDocument>(
    '/api/denominational-documents',
    document
  );

  return response.data;
};

/**
 * Bulk upload a large document with automatic chunking (admin only)
 */
export const bulkUploadDocument = async (
  request: BulkUploadRequest
): Promise<{
  message: string;
  chunks_created: number;
  document_title: string;
  denomination_id: string;
  documents: DenominationalDocument[];
}> => {
  const response = await apiClient.post(
    '/api/denominational-documents/bulk-upload',
    request
  );

  return response.data;
};

/**
 * Update a document (admin only)
 */
export const updateDocument = async (
  documentId: string,
  updates: Partial<Omit<DenominationalDocument, 'id' | 'created_at' | 'updated_at'>>
): Promise<DenominationalDocument> => {
  const response = await apiClient.put<DenominationalDocument>(
    `/api/denominational-documents/${documentId}`,
    updates
  );

  return response.data;
};

/**
 * Delete a document (admin only)
 */
export const deleteDocument = async (documentId: string): Promise<void> => {
  await apiClient.delete(`/api/denominational-documents/${documentId}`);
};

/**
 * Get document statistics
 */
export const getDocumentStats = async (): Promise<{
  total_documents: number;
  message: string;
}> => {
  const response = await apiClient.get('/api/denominational-documents/stats/summary');
  return response.data;
};
